import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAttendance } from '../contexts/AttendanceContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Clock, Calendar, MapPin, AlertCircle,
  FileText, Camera, ChevronDown, ChevronUp, Timer, LogIn, LogOut, BarChart3,
  Award, Target,
} from 'lucide-react';
import { formatDate, formatTime, calculateDuration } from '../utils/dateFormatter';
import { formatDistance } from '../utils/distance';
import { useLanguage } from '../contexts/LanguageContext';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from 'date-fns';
import type { AttendanceRecord } from '../types';
import PayslipSection from '../components/PayslipSection';

type FilterType = 'all' | 'week' | 'month';

function MonthHeatmap({ history }: { history: AttendanceRecord[] }): React.JSX.Element {
  const { t } = useLanguage();
  const now = new Date();
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  return (
    <div className="flex flex-wrap gap-1.5">
      {days.map((day) => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const record = history.find((r) => r.date === dayStr);
        const isFuture = day > now;
        const isToday = isSameDay(day, now);
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
        let bg = 'bg-red-100 dark:bg-red-950/40';
        let tooltip = format(day, 'EEE, d MMM');
        if (isFuture) {
          bg = 'bg-muted/90';
        } else if (record) {
          bg = record.status === 'on_time'
            ? 'bg-emerald-400 dark:bg-emerald-500'
            : 'bg-amber-400 dark:bg-amber-500';
          tooltip += ' • ' + (record.status === 'on_time' ? t.onTime : t.late);
        } else if (isWeekend) {
          bg = 'bg-red-500/15';
        } else {
          bg = 'bg-orange-500/15 dark:bg-orange-950/30';
          tooltip += ' • ' + t.absent;
        }
        return (
          <div
            key={dayStr}
            title={tooltip}
            className={`relative h-6 w-6 rounded ${bg} ${
              isToday
                ? 'ring-2 ring-primary ring-offset-1 ring-offset-background scale-110'
                : 'hover:scale-125'
            } transition-all duration-150 cursor-default flex-shrink-0`}
          >
            {isToday && (
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function RecordDetailRow({ icon: Icon, label, value, accent }: {
  icon: React.ElementType; label: string; value: string; accent: string;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${accent} flex-shrink-0`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-medium">{label}</p>
        <p className="text-sm font-bold">{value}</p>
      </div>
    </div>
  );
}

export default function Attendance(): React.JSX.Element {
  const { history, monthlySummary, loadHistory, loadMonthlySummary } = useAttendance();
  const { t } = useLanguage();
  const [filter, setFilter] = useState<FilterType>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const [filterIndicatorStyle, setFilterIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });

  useEffect(() => {
    const updateIndicator = () => {
      const container = filterContainerRef.current;
      if (!container) return;
      const buttons = container.querySelectorAll('button[data-filter-role="button"]');
      const activeIndex = (['all', 'month', 'week'] as const).indexOf(filter);
      const activeBtn = buttons[activeIndex] as HTMLElement | undefined;
      if (!activeBtn) return;
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeBtn.getBoundingClientRect();
      setFilterIndicatorStyle({
        left: activeRect.left - containerRect.left,
        width: activeRect.width,
        opacity: 1,
      });
    };
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [filter]);

  // Load page-specific data on mount (lazy — skips if already fetched by Dashboard).
  useEffect(() => {
    void loadHistory();
    void loadMonthlySummary();
  }, [loadHistory, loadMonthlySummary]);

  const getFilteredRecords = (): AttendanceRecord[] => {
    const now = new Date();
    switch (filter) {
      case 'week': {
        const weekStart = startOfWeek(now, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
        return history.filter((r) => {
          const date = new Date(r.date);
          return date >= weekStart && date <= weekEnd;
        });
      }
      case 'month': {
        const monthStart = startOfMonth(now);
        const monthEnd = endOfMonth(now);
        return history.filter((r) => {
          const date = new Date(r.date);
          return date >= monthStart && date <= monthEnd;
        });
      }
      default:
        return history;
    }
  };

  const records = getFilteredRecords().sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const onTimeRate = monthlySummary && monthlySummary.totalDays > 0
    ? Math.round((monthlySummary.onTimeDays / monthlySummary.totalDays) * 100)
    : 0;
  const toggleExpand = (id: string): void => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Group records by month for timeline  
  const groupedRecords = useMemo(() => {
    const groups: Map<string, AttendanceRecord[]> = new Map();
    for (const record of records) {
      const key = format(new Date(record.date), 'MMMM yyyy');
      const existing = groups.get(key) ?? [];
      existing.push(record);
      groups.set(key, existing);
    }
    return groups;
  }, [records]);

  return (
    <div className="space-y-4 animate-fade-in pb-8">

      {/* ═══════════════════════════════════════
          HERO HEADER — Brand Gradient
      ═══════════════════════════════════════ */}
      <div
        className="relative rounded-2xl overflow-hidden bg-premium-hero"
      >
        <div className="relative px-5 pt-5 pb-6 z-10">
          {/* Title row */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 shadow-inner">
                  <Clock className="h-4 w-4 text-white" />
                </div>
                <span className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">{t.attendanceHistory}</span>
              </div>
              <h1 className="text-[26px] font-black text-white leading-tight tracking-tight">
                {format(new Date(), 'MMMM yyyy')}
              </h1>
              <p className="text-[12px] text-white/70 mt-0.5 font-medium">{t.attendanceHistoryDesc}</p>
            </div>
            {/* Award badge — always shown */}
            <div
              className="flex flex-col items-center justify-center rounded-2xl px-3.5 py-2.5 flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(10px)' }}
            >
              <Award className="h-5 w-5 text-yellow-200 mb-0.5" />
              <span className="text-lg font-black text-white leading-none">{onTimeRate}<span className="text-xs font-bold">%</span></span>
              <span className="text-[9px] text-white/70 font-semibold mt-0.5 uppercase tracking-wide">{t.onTime}</span>
            </div>
          </div>

          {/* Mini stats strip */}
          {monthlySummary && (
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: monthlySummary.totalDays, label: t.totalDaysWorked, color: 'text-white' },
                { value: monthlySummary.onTimeDays, label: t.onTime, color: 'text-emerald-200' },
                { value: monthlySummary.lateDays, label: t.late, color: 'text-amber-200' },
              ].map(({ value, label, color }) => (
                <div
                  key={label}
                  className="rounded-xl p-2.5 text-center"
                  style={{ background: 'rgba(0,0,0,0.15)' }}
                >
                  <p className={`text-2xl font-black ${color} leading-none`}>{value}</p>
                  <p className="text-[10px] text-white/65 mt-1 font-medium leading-tight">{label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════
          PERFORMANCE + HOURS — Stat Cards
      ═══════════════════════════════════════ */}
      {monthlySummary && (
        <div className="grid grid-cols-2 gap-3">
          {/* Punctuality card */}
          <Card className="border border-emerald-100 dark:border-emerald-900/40 bg-white dark:bg-card shadow-sm overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #10b981, #34d399)' }} />
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                  <Target className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{t.performance}</span>
              </div>
              <p className="text-4xl font-black text-emerald-600 dark:text-emerald-400 leading-none">
                {onTimeRate}<span className="text-xl font-bold">%</span>
              </p>
              <div className="mt-2.5 h-1.5 w-full rounded-full bg-emerald-100 dark:bg-emerald-900/30 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${onTimeRate}%`, background: 'linear-gradient(90deg, #10b981, #34d399)' }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                {monthlySummary.onTimeDays} / {monthlySummary.totalDays} {t.totalDaysWorked}
              </p>
            </CardContent>
          </Card>

          {/* Hours card */}
          <Card className="border border-orange-100 dark:border-orange-900/40 bg-white dark:bg-card shadow-sm overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'var(--gradient-primary)' }} />
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{t.average}</span>
              </div>
              <p className="text-4xl font-black text-primary leading-none">
                {monthlySummary.totalHours}<span className="text-xl font-bold">h</span>
              </p>
              <div className="mt-2.5 h-1.5 w-full rounded-full bg-primary/10 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${Math.min(100, (monthlySummary.totalHours / (monthlySummary.totalDays * 8)) * 100)}%`,
                    background: 'var(--gradient-primary)',
                  }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                {t.avgPerDay(String(monthlySummary.avgHoursPerDay))}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════
          MONTHLY HEATMAP
      ═══════════════════════════════════════ */}
      <Card className="border border-border/60 bg-white dark:bg-card shadow-sm">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: 'var(--gradient-primary)' }}>
                <BarChart3 className="h-3.5 w-3.5 text-white" />
              </div>
              {t.monthlyOverview}
            </CardTitle>
            <span
              className="text-[11px] font-bold px-2.5 py-0.5 rounded-full text-white"
              style={{ background: 'var(--gradient-primary)' }}
            >
              {format(new Date(), 'MMMM yyyy')}
            </span>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <MonthHeatmap history={history} />
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/40 flex-wrap">
            {[
              { color: 'bg-emerald-400', label: t.onTime },
              { color: 'bg-amber-400', label: t.late },
              { color: 'bg-red-300', label: t.absent },
              { color: 'bg-muted', label: t.upcoming },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground">
                <div className={`h-3 w-3 rounded ${color}`} />
                {label}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════
          FILTER TABS + RECORD COUNT
      ═══════════════════════════════════════ */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Pill tabs */}
        <div
          ref={filterContainerRef}
          className="relative flex items-center bg-muted/50 rounded-full p-1 gap-0.5 border border-border/40"
        >
          {/* Sliding active indicator */}
          <div
            className="absolute top-1 bottom-1 rounded-full transition-all duration-300 ease-out"
            style={{
              left: filterIndicatorStyle.left,
              width: filterIndicatorStyle.width,
              background: 'var(--gradient-primary)',
              boxShadow: '0 2px 8px rgba(232,130,0,0.35)',
              opacity: filterIndicatorStyle.opacity,
            }}
          />
          {(['all', 'month', 'week'] as FilterType[]).map((f) => (
            <button
              key={f}
              data-filter-role="button"
              onClick={() => setFilter(f)}
              className={`relative z-10 px-4 py-1.5 rounded-full text-xs font-bold transition-colors duration-200 ${
                filter === f ? 'text-white' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f === 'all' ? t.all : f === 'month' ? t.month : t.week}
            </button>
          ))}
        </div>

        {/* Record summary chips */}
        <div className="flex items-center gap-2 text-[11px]">
          <span className="flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 rounded-full px-2.5 py-1 font-semibold border border-emerald-100 dark:border-emerald-900/30">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {records.filter((r) => r.status === 'on_time').length} {t.onTime}
          </span>
          <span className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 rounded-full px-2.5 py-1 font-semibold border border-amber-100 dark:border-amber-900/30">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            {records.filter((r) => r.status === 'late').length} {t.late}
          </span>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          PAYSLIPS
      ═══════════════════════════════════════ */}
      <div>
        <div className="flex items-center gap-2.5 mb-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: 'var(--gradient-primary)' }}>
            <FileText className="h-4 w-4 text-white" />
          </div>
          <h2 className="text-base font-bold text-foreground">{t.payslips}</h2>
        </div>
        <PayslipSection />
      </div>

      {/* ═══════════════════════════════════════
          ATTENDANCE TIMELINE
      ═══════════════════════════════════════ */}
      {records.length === 0 ? (
        <Card className="border border-dashed border-border/60">
          <CardContent className="py-16">
            <div className="flex flex-col items-center text-center">
              <div className="relative h-20 w-20 mb-5">
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{ background: 'var(--gradient-primary)', opacity: 0.15 }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Calendar className="h-9 w-9 text-primary/40" />
                </div>
              </div>
              <h3 className="text-base font-bold mb-1.5">{t.noRecords}</h3>
              <p className="text-sm text-muted-foreground max-w-xs mx-auto">{t.noRecordsDesc}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {Array.from(groupedRecords.entries()).map(([monthLabel, monthRecords]) => (
            <div key={monthLabel}>
              {/* ── Month group heading ── */}
              <div className="flex items-center gap-2 mb-3">
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-lg flex-shrink-0 shadow-sm"
                  style={{ background: 'var(--gradient-primary)' }}
                >
                  <Calendar className="h-3.5 w-3.5 text-white" />
                </div>
                <h3 className="text-sm font-extrabold text-foreground">{monthLabel}</h3>
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                  style={{ background: 'var(--gradient-primary)' }}
                >
                  {monthRecords.length}
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
              </div>

              {/* ── Timeline list ── */}
              <div className="relative ml-3.5 pl-5 space-y-2.5"
                style={{ borderLeft: '2px solid hsl(var(--border) / 0.5)' }}>
                {monthRecords.map((record) => {
                  const isExpanded = expandedId === record.id;
                  const isOnTime = record.status === 'on_time';
                  const dotColor = isOnTime ? '#10b981' : '#f59e0b';
                  return (
                    <div
                      key={record.id}
                      className="relative"
                      data-testid={`attendance-record-${record.id}`}
                    >
                      {/* Timeline connector dot */}
                      <div
                        className="absolute -left-[25px] top-[17px] h-3.5 w-3.5 rounded-full border-[2.5px] border-background shadow"
                        style={{ backgroundColor: dotColor }}
                      />

                      <Card
                        className={`overflow-hidden transition-all duration-200 cursor-pointer bg-white dark:bg-card ${
                          isExpanded
                            ? 'shadow-md ring-1 ring-primary/20'
                            : 'shadow-sm hover:shadow-md hover:-translate-y-px'
                        }`}
                        style={{ borderLeft: `3px solid ${dotColor}` }}
                        onClick={() => toggleExpand(record.id)}
                      >
                        {/* ── Summary row ── */}
                        <div className="p-3.5">
                          <div className="flex items-center gap-3">
                            {/* Photo or icon */}
                            {record.checkInRecord?.photoDataUrl ? (
                              <img
                                src={record.checkInRecord.photoDataUrl}
                                alt="Check-in"
                                className="h-11 w-11 rounded-xl object-cover border-2 border-border flex-shrink-0"
                                data-testid="record-photo"
                              />
                            ) : (
                              <div
                                className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{ background: isOnTime ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)' }}
                              >
                                <Camera className="h-5 w-5" style={{ color: dotColor }} />
                              </div>
                            )}

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                <span className="text-sm font-bold text-foreground">{formatDate(record.date)}</span>
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-extrabold uppercase tracking-wide text-white"
                                  style={{ backgroundColor: dotColor }}
                                >
                                  {isOnTime ? t.onTime : t.late}
                                </span>
                              </div>
                              <p className="text-[11px] text-muted-foreground">{format(new Date(record.date), 'EEEE')}</p>

                              {/* Clock in/out/duration row */}
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                <span className="flex items-center gap-1 text-[11px] font-bold text-foreground/80">
                                  <LogIn className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                                  {formatTime(record.clockIn)}
                                </span>
                                {record.clockOut && (
                                  <>
                                    <span className="text-muted-foreground/30 font-bold">·</span>
                                    <span className="flex items-center gap-1 text-[11px] font-bold text-foreground/80">
                                      <LogOut className="h-3 w-3 text-amber-500 flex-shrink-0" />
                                      {formatTime(record.clockOut)}
                                    </span>
                                    <span className="text-muted-foreground/30 font-bold">·</span>
                                    <span className="flex items-center gap-1 text-[11px] font-black text-primary">
                                      <Timer className="h-3 w-3 flex-shrink-0" />
                                      {calculateDuration(record.clockIn, record.clockOut)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Chevron */}
                            <div className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all ${
                              isExpanded ? 'bg-primary/10' : 'bg-muted/40'
                            }`}>
                              {isExpanded
                                ? <ChevronUp className="h-3.5 w-3.5 text-primary" />
                                : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                            </div>
                          </div>
                        </div>

                        {/* ── Expanded detail ── */}
                        {isExpanded && (
                          <div
                            className="border-t px-3.5 py-3 animate-fade-in"
                            style={{
                              borderColor: isOnTime ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                              background: isOnTime ? 'rgba(16,185,129,0.04)' : 'rgba(245,158,11,0.04)',
                            }}
                          >
                            {/* Time detail row */}
                            <div className="grid grid-cols-3 gap-2.5 mb-3">
                              <RecordDetailRow
                                icon={LogIn}
                                label={t.checkIn}
                                value={formatTime(record.clockIn)}
                                accent="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400"
                              />
                              {record.clockOut ? (
                                <>
                                  <RecordDetailRow
                                    icon={LogOut}
                                    label={t.checkOut}
                                    value={formatTime(record.clockOut)}
                                    accent="bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400"
                                  />
                                  <RecordDetailRow
                                    icon={Timer}
                                    label={t.duration}
                                    value={calculateDuration(record.clockIn, record.clockOut)}
                                    accent="bg-primary/10 text-primary"
                                  />
                                </>
                              ) : (
                                <div className="col-span-2 flex items-center pl-1">
                                  <span className="text-xs text-muted-foreground italic">—</span>
                                </div>
                              )}
                            </div>

                            {/* Location cards */}
                            <div className="grid sm:grid-cols-2 gap-2">
                              {record.checkInRecord && (
                                <div className="p-2.5 bg-white/70 dark:bg-card/70 rounded-xl border border-border/40">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <MapPin className="h-3 w-3 text-emerald-500" />
                                    <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">{t.checkInLocation}</span>
                                  </div>
                                  <p className="text-xs font-bold">{formatDistance(record.checkInRecord.distance)} {t.fromOffice}</p>
                                  {record.checkInRecord.lateMinutes > 0 && (
                                    <p className="text-[10px] text-amber-500 mt-1 flex items-center gap-1 font-semibold">
                                      <AlertCircle className="h-3 w-3" />
                                      {t.lateBy(record.checkInRecord.lateMinutes)}
                                    </p>
                                  )}
                                </div>
                              )}
                              {record.checkOutRecord && (
                                <div className="p-2.5 bg-white/70 dark:bg-card/70 rounded-xl border border-border/40">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <MapPin className="h-3 w-3 text-amber-500" />
                                    <span className="text-[9px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest">{t.checkOutLocation}</span>
                                  </div>
                                  <p className="text-xs font-bold">{formatDistance(record.checkOutRecord.distance)} {t.fromOffice}</p>
                                  {record.checkOutRecord.earlyLeaveMinutes > 0 && (
                                    <p className="text-[10px] text-amber-500 mt-1 flex items-center gap-1 font-semibold">
                                      <AlertCircle className="h-3 w-3" />
                                      {t.earlyBy(record.checkOutRecord.earlyLeaveMinutes)}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Attendance photos */}
                            {(record.checkInRecord?.photoDataUrl || record.checkOutRecord?.photoDataUrl) && (
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                {record.checkInRecord?.photoDataUrl ? (
                                  <div>
                                    <p className="text-[9px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                                      <Camera className="h-2.5 w-2.5" />
                                      {t.checkIn}
                                    </p>
                                    <img
                                      src={record.checkInRecord.photoDataUrl}
                                      alt="Check-in photo"
                                      className="w-full aspect-square object-cover rounded-xl border border-border/40 shadow-sm"
                                    />
                                  </div>
                                ) : (
                                  <div />
                                )}
                                {record.checkOutRecord?.photoDataUrl ? (
                                  <div>
                                    <p className="text-[9px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                                      <Camera className="h-2.5 w-2.5" />
                                      {t.checkOut}
                                    </p>
                                    <img
                                      src={record.checkOutRecord.photoDataUrl}
                                      alt="Check-out photo"
                                      className="w-full aspect-square object-cover rounded-xl border border-border/40 shadow-sm"
                                    />
                                  </div>
                                ) : (
                                  <div />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

